import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Copy, Eye, EyeOff, RefreshCw, Video, ExternalLink, Radio, AlertTriangle } from "lucide-react";

/**
 * OBS Stream Setup — shows the streamer their RTMPS ingest URL + private stream
 * key, plus alternative WHIP + SRT ingest URLs for advanced setups.
 *
 * Streamer copies these into OBS: Settings → Stream → Service: Custom.
 * OBS pushes RTMPS to Cloudflare, Cloudflare transcodes, and viewers see the
 * live stream on the channel page via HLS (see /components/HlsPlayer.jsx).
 */
export default function ObsSetup() {
  const { user } = useAuth();
  const [live, setLive] = useState(null);           // { rtmps_url, rtmps_key, whip_url, ... }
  const [status, setStatus] = useState({ is_live: false });
  const [loading, setLoading] = useState(true);
  const [keyVisible, setKeyVisible] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/obs/live-input");
        setLive(r.data);
      } catch (e) {
        if (e?.response?.status !== 404) setErr(e?.response?.data?.detail || "Failed to load stream setup");
      } finally { setLoading(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !live) return undefined;
    const poll = () => api.get(`/obs/status/${user.username}`)
      .then(r => setStatus(r.data))
      .catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [user, live]);

  const provision = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.post("/obs/live-input");
      setLive(r.data);
      toast.success("OBS stream key created.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Provisioning failed");
    } finally { setLoading(false); }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotate stream key? Your current OBS setup will stop working until you paste the new key.")) return;
    setLoading(true);
    try {
      const r = await api.post("/obs/live-input/reset-key");
      setLive(r.data);
      toast.success("New key generated.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Rotation failed");
    } finally { setLoading(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable OBS ingest for your channel? You can re-enable at any time.")) return;
    setLoading(true);
    try {
      await api.delete("/obs/live-input");
      setLive(null);
      toast.success("OBS ingest disabled.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Disable failed");
    } finally { setLoading(false); }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val || "");
    toast.success(`${label} copied`);
  };

  if (loading) return <div className="text-sm text-zinc-400">Loading OBS setup…</div>;

  return (
    <div className="sc-card p-6" data-testid="obs-setup">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>External Streaming</div>
          <h2 className="sc-heading text-2xl font-black flex items-center gap-2">
            <Video className="w-6 h-6 text-[color:var(--sc-cyan)]" /> OBS Stream Setup
          </h2>
          <div className="text-xs text-zinc-400 mt-1">Stream with OBS Studio, Streamlabs, or any RTMPS/WHIP-compatible encoder.</div>
        </div>
        {live && (
          <div className={`px-3 py-1.5 rounded-sm border sc-mono text-xs uppercase tracking-widest flex items-center gap-2 ${status.is_live ? "border-[color:var(--sc-pink)] bg-[color:var(--sc-pink)]/10 text-[color:var(--sc-pink)]" : "border-white/10 text-zinc-400"}`} data-testid="obs-status-chip">
            {status.is_live ? <><span className="live-dot" /> LIVE VIA OBS</> : <><Radio className="w-3 h-3" /> OFFLINE</>}
          </div>
        )}
      </div>

      {err && (
        <div className="flex items-start gap-2 border border-[color:var(--sc-pink)]/40 bg-[color:var(--sc-pink)]/5 rounded-sm p-3 mb-4" data-testid="obs-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-[color:var(--sc-pink)] flex-shrink-0" />
          <div className="text-xs text-zinc-200">{err}</div>
        </div>
      )}

      {!live ? (
        <div className="text-center py-6" data-testid="obs-empty-state">
          <div className="text-sm text-zinc-400 mb-4">
            Provision a private stream key to broadcast from OBS. Cloudflare handles the ingest;
            viewers see your stream on your channel page automatically.
          </div>
          <button onClick={provision} disabled={loading} className="sc-btn text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="obs-provision-btn">
            <Video className="w-4 h-4" /> Enable OBS Streaming
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* RTMPS URL */}
          <Field label="RTMPS Server URL" value={live.rtmps_url} testid="obs-rtmps-url" onCopy={() => copy(live.rtmps_url, "RTMPS URL")} />

          {/* Stream Key (masked by default) */}
          <div>
            <label className="sc-label mb-1 block">Stream Key <span className="text-[color:var(--sc-pink)]">(keep private)</span></label>
            <div className="flex items-center gap-2">
              <input
                readOnly
                type={keyVisible ? "text" : "password"}
                value={live.rtmps_key || ""}
                className="sc-input flex-1 text-sm sc-mono"
                data-testid="obs-stream-key"
                onFocus={(e) => e.target.select()}
              />
              <button onClick={() => setKeyVisible(v => !v)} className="sc-btn-ghost p-2" title={keyVisible ? "Hide" : "Show"} data-testid="obs-toggle-key">
                {keyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => copy(live.rtmps_key, "Stream key")} className="sc-btn-ghost p-2" data-testid="obs-copy-key">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Optional: WHIP for OBS 30+ */}
          {live.whip_url && (
            <Field label="WHIP URL (OBS 30+ / WebRTC low-latency)" value={live.whip_url} testid="obs-whip-url" onCopy={() => copy(live.whip_url, "WHIP URL")} optional />
          )}

          {/* Setup guide */}
          <div className="border border-white/10 rounded-sm p-3 bg-black/20">
            <div className="sc-label mb-2 text-[color:var(--sc-cyan)]">How to configure OBS</div>
            <ol className="text-xs text-zinc-300 space-y-1 list-decimal pl-4">
              <li>Open OBS → <span className="sc-mono">Settings → Stream</span></li>
              <li>Service: <span className="sc-mono">Custom…</span></li>
              <li>Server: paste the RTMPS URL above</li>
              <li>Stream Key: paste the key above</li>
              <li>Apply → click <span className="sc-mono">Start Streaming</span> in OBS</li>
              <li>Your channel page shows the LIVE badge in ~10-15 seconds</li>
            </ol>
            <a href="https://developers.cloudflare.com/stream/stream-live/start-stream-live/" target="_blank" rel="noreferrer" className="text-xs text-[color:var(--sc-cyan)] mt-2 inline-flex items-center gap-1 hover:underline">
              Cloudflare Stream docs <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Danger zone */}
          <div className="flex gap-2 pt-3 border-t border-white/10">
            <button onClick={rotateKey} disabled={loading} className="sc-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="obs-rotate-btn">
              <RefreshCw className="w-3 h-3" /> Rotate Key
            </button>
            <button onClick={disable} disabled={loading} className="sc-btn-ghost text-xs text-[color:var(--sc-pink)]" data-testid="obs-disable-btn">
              Disable OBS Ingest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, testid, onCopy, optional }) {
  return (
    <div>
      <label className="sc-label mb-1 block">
        {label} {optional && <span className="text-zinc-500 normal-case">(optional)</span>}
      </label>
      <div className="flex items-center gap-2">
        <input readOnly value={value || ""} className="sc-input flex-1 text-sm sc-mono" data-testid={testid} onFocus={(e) => e.target.select()} />
        <button onClick={onCopy} className="sc-btn-ghost p-2"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
